/**
 * Derive allowed SIM types from device compatibility caps and resolve defaults.
 */

export type SimCapabilities = {
  eSIMOnly?: boolean;
  singleSIMWithESIM?: boolean;
  singleSIM?: boolean;
  dualSIM?: boolean;
};

export function getSimTypeAllowance(caps: SimCapabilities | null | undefined) {
  if (!caps) {
    return { eSimAllowed: false, physicalSimAllowed: false };
  }
  const eSimAllowed = Boolean(caps.singleSIMWithESIM || caps.eSIMOnly);
  const physicalSimAllowed = Boolean(
    !caps.eSIMOnly && (caps.singleSIM || caps.dualSIM || caps.singleSIMWithESIM)
  );
  return { eSimAllowed, physicalSimAllowed };
}

/** When only one option exists, return it; when both, require explicit choice (empty). */
export function pickDefaultSimType(
  eSimAllowed: boolean,
  physicalSimAllowed: boolean
): "" | "eSim" | "sim" {
  if (eSimAllowed && !physicalSimAllowed) return "eSim";
  if (!eSimAllowed && physicalSimAllowed) return "sim";
  return "";
}

/** Keep current value only if still valid; otherwise pick default. */
export function resolveSimTypeForCapabilities(
  current: string,
  eSimAllowed: boolean,
  physicalSimAllowed: boolean
): "" | "eSim" | "sim" {
  if (current === "eSim" && eSimAllowed) return "eSim";
  if (current === "sim" && physicalSimAllowed) return "sim";
  return pickDefaultSimType(eSimAllowed, physicalSimAllowed);
}

/** Value safe to pass to RadioGroup (no "selected" state for a disabled option). */
export function effectiveRadioSimValue(
  stored: string,
  eSimAllowed: boolean,
  physicalSimAllowed: boolean
): string {
  if (stored === "eSim" && eSimAllowed) return "eSim";
  if (stored === "sim" && physicalSimAllowed) return "sim";
  return "";
}

/** Build a single-line compatibility payload for outlet state and Redux. */
export function buildLineCompatibility(
  row: Record<string, unknown>,
  simType: string,
  imeiNumber = ""
) {
  return {
    ...row,
    simType,
    is_esim: simType === "eSim" ? "Y" : "N",
    deviceId: simType === "sim" ? imeiNumber : "",
    enrollment_type:
      simType === "sim" && imeiNumber ? "HANDOVER" : "SHIPMENT",
  };
}

/** Shallow compare fields that phone-selector sync effects care about. */
export function isSameCompatPayload(
  a: Record<string, unknown> | null | undefined,
  b: Record<string, unknown> | null | undefined
): boolean {
  if (!a || !b) return a === b;
  return (
    a.simType === b.simType &&
    a.is_esim === b.is_esim &&
    (a.deviceId ?? "") === (b.deviceId ?? "") &&
    (a.enrollment_type ?? "") === (b.enrollment_type ?? "") &&
    a._id === b._id
  );
}
